import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';

import { HeaderComponent } from '@shared/components/header/header.component';
import { FooterActionComponent } from '@shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { PageScrollSpyComponent, ScrollSpySection } from '@shared/components/page-scroll-spy/page-scroll-spy.component';

import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { TipoValorDeclaradoExpedicaoEnum } from '@shared/enums/vendas/tipo-valor-declarado-expedicao.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { ConfigExpedicaoService } from '@shared/services/apis/vendas/config-expedicao.service';
import { ConfigVendasGeralService } from '@shared/services/apis/vendas/config-vendas-geral.service';
import { ConfigExpedicaoRequest } from '@shared/services/requests/vendas/config-expedicao.request';
import { TipoValorDeclaradoExpedicao } from '@shared/services/models/vendas/config-expedicao.model';
import { TipoDocumentoImpressao } from '@shared/services/responses/vendas/forma-envio-lookups.response';

interface VariavelMensagem {
  codigo: string;
  descricao: string;
}

/** Bloco de notificação por e-mail (rastreio, entrega, XML): cada um com ativação, assunto e mensagem próprios. */
interface BlocoEmail {
  titulo: string;
  controleEnvio: string;
  rotuloEnvio: string;
  controleAssunto: string;
  controleMensagem: string;
  editor: Editor;
  variaveis: VariavelMensagem[];
  exibirVariaveis: boolean;
}

@Component({
  selector: 'app-config-expedicao',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    NgxEditorModule,
    HeaderComponent,
    FooterActionComponent,
    LoadingBoxComponent,
    AlertCardComponent,
    PageScrollSpyComponent,
  ],
  templateUrl: './config-expedicao.component.html',
  styleUrls: ['./config-expedicao.component.css'],
})
export class ConfigExpedicaoComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Vendas;
  public form: FormGroup;
  public isLoading = false;
  public isSaving = false;

  private idConfigExpedicao = 0;

  // Só libera o Salvar depois que a configuração da empresa realmente chegou (mesmo cuidado de config-pedido-venda).
  public configCarregada = false;
  public isTrabalhaComExpedicao = true;

  public opcoesTipoDocumentoImpressao: TipoDocumentoImpressao[] = [];
  public opcoesTipoValorDeclaradoExpedicao: TipoValorDeclaradoExpedicao[] = [];

  public sections: ScrollSpySection[] = [
    { id: 'valor-conferencia', label: 'Valor e conferência' },
    { id: 'postagem-valor-declarado', label: 'Postagem e valor declarado' },
    { id: 'notificacoes-email', label: 'Notificações por e-mail' },
    { id: 'impressao', label: 'Impressão' },
  ];

  // Origem da navegação: 'gerais' quando aberta pelo atalho de Configurações Gerais de Vendas; senão o menu.
  private origem: string | null = null;
  public tooltipVoltar = 'Voltar para Configurações de Vendas';

  public toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3'] }],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  // Variáveis substituídas no envio do e-mail (mesmo formato de config-financas/envio-documentos).
  private readonly variaveisPedido: VariavelMensagem[] = [
    { codigo: '[IDENTIFICACAO]', descricao: 'Identificação da expedição' },
    { codigo: '[CODIGO_RASTREIO]', descricao: 'Código de rastreio' },
    { codigo: '[NUMERO_ORIGEM]', descricao: 'Número da origem' },
    { codigo: '[TRANSPORTADORA]', descricao: 'Transportadora' },
    { codigo: '[NOME_CLIENTE]', descricao: 'Nome do cliente' },
    { codigo: '[DOCUMENTO_CLIENTE]', descricao: 'CNPJ/CPF do cliente' },
    { codigo: '[NUMERO_PEDIDO_ECOMMERCE]', descricao: 'Número do pedido no e-commerce' },
    { codigo: '[FORMA_FRETE]', descricao: 'Forma de frete' },
    { codigo: '[FORMA_ENVIO]', descricao: 'Forma de envio' },
    { codigo: '[URL_RASTREAMENTO]', descricao: 'URL de rastreamento' },
  ];

  public blocosEmail: BlocoEmail[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private configExpedicaoService: ConfigExpedicaoService,
    private configVendasGeralService: ConfigVendasGeralService,
    private alertService: AlertService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.form = this.formBuilder.group({
      // Valor e conferência
      isMostrarValorMercadoria: [false],
      isHabilitarConferenciaPedido: [false],
      isImprimirPedidoCanceladoListaColeta: [false],
      // Postagem e valor declarado
      idTipoValorDeclaradoExpedicao: [TipoValorDeclaradoExpedicaoEnum.SemValorDeclarado],
      isImprimirValorDeclaradoEtiqueta: [false],
      // Notificações por e-mail
      isEnviarEmailCodigoRastreio: [false],
      assuntoEmailCodigoRastreio: ['Código de rastreio', [Validators.required, Validators.maxLength(255)]],
      mensagemEmailCodigoRastreioHtml: [''],
      isEnviarEmailEntrega: [false],
      assuntoEmailEntrega: ['Pedido entregue', [Validators.required, Validators.maxLength(255)]],
      mensagemEmailEntregaHtml: [''],
      isEnviarEmailXmlTransportadora: [false],
      assuntoEmailXmlTransportadora: ['XML referente [IDENTIFICACAO]', [Validators.required, Validators.maxLength(255)]],
      mensagemEmailXmlTransportadoraHtml: [''],
      // Impressão
      listTipoDocumentoImpressao: [[] as number[]],
    });
  }

  ngOnInit(): void {
    this.origem = this.route.snapshot.queryParamMap.get('origem');
    if (this.origem === 'gerais') this.tooltipVoltar = 'Voltar para Configurações Gerais de Vendas';
    // Instâncias de ngx-editor não sobrevivem à saída da tela: criadas aqui e destruídas no ngOnDestroy.
    this.blocosEmail = [
      {
        titulo: 'Código de rastreio',
        controleEnvio: 'isEnviarEmailCodigoRastreio',
        rotuloEnvio: 'Enviar código de rastreio por e-mail ao concluir a expedição',
        controleAssunto: 'assuntoEmailCodigoRastreio',
        controleMensagem: 'mensagemEmailCodigoRastreioHtml',
        editor: new Editor(),
        variaveis: this.variaveisPedido,
        exibirVariaveis: false,
      },
      {
        titulo: 'Entrega',
        controleEnvio: 'isEnviarEmailEntrega',
        rotuloEnvio: 'Enviar e-mail ao marcar pedido como entregue',
        controleAssunto: 'assuntoEmailEntrega',
        controleMensagem: 'mensagemEmailEntregaHtml',
        editor: new Editor(),
        variaveis: this.variaveisPedido,
        exibirVariaveis: false,
      },
      {
        titulo: 'XML para transportadora',
        controleEnvio: 'isEnviarEmailXmlTransportadora',
        rotuloEnvio: 'Enviar automaticamente o XML para transportadora quando concluir a expedição',
        controleAssunto: 'assuntoEmailXmlTransportadora',
        controleMensagem: 'mensagemEmailXmlTransportadoraHtml',
        editor: new Editor(),
        variaveis: this.variaveisPedido.filter(v => v.codigo === '[IDENTIFICACAO]'),
        exibirVariaveis: false,
      },
    ];

    this.carregarDados();
  }

  ngOnDestroy(): void {
    this.blocosEmail.forEach(bloco => bloco.editor.destroy());
  }

  private carregarDados(): void {
    this.isLoading = true;
    forkJoin({
      config: this.configExpedicaoService.get().pipe(catchError(() => of(null))),
      lookups: this.configExpedicaoService.getLookups().pipe(catchError(() => of(null))),
      geral: this.configVendasGeralService.getLookup().pipe(catchError(() => of(null))),
    }).subscribe({
      next: res => {
        this.isLoading = false;

        if (res.lookups && !res.lookups.isError) {
          this.opcoesTipoDocumentoImpressao = res.lookups.listTipoDocumentoImpressao || [];
          this.opcoesTipoValorDeclaradoExpedicao = res.lookups.listTipoValorDeclaradoExpedicao || [];
        } else if (res.lookups?.isError) {
          this.alertService.show('Aviso', res.lookups.errorDescription, AlertType.warning);
        }

        if (res.geral && !res.geral.isError && res.geral.configVendasGeral) {
          this.isTrabalhaComExpedicao = res.geral.configVendasGeral.isTrabalhaComExpedicao;
        }

        if (res.config && !res.config.isError && res.config.configExpedicao) {
          const c = res.config.configExpedicao;
          this.idConfigExpedicao = c.idConfigExpedicao;
          this.form.patchValue({
            isMostrarValorMercadoria: c.isMostrarValorMercadoria,
            isHabilitarConferenciaPedido: c.isHabilitarConferenciaPedido,
            isImprimirPedidoCanceladoListaColeta: c.isImprimirPedidoCanceladoListaColeta,
            idTipoValorDeclaradoExpedicao: c.idTipoValorDeclaradoExpedicao,
            isImprimirValorDeclaradoEtiqueta: c.isImprimirValorDeclaradoEtiqueta,
            isEnviarEmailCodigoRastreio: c.isEnviarEmailCodigoRastreio,
            assuntoEmailCodigoRastreio: c.assuntoEmailCodigoRastreio,
            mensagemEmailCodigoRastreioHtml: c.mensagemEmailCodigoRastreioHtml || '',
            isEnviarEmailEntrega: c.isEnviarEmailEntrega,
            assuntoEmailEntrega: c.assuntoEmailEntrega,
            mensagemEmailEntregaHtml: c.mensagemEmailEntregaHtml || '',
            isEnviarEmailXmlTransportadora: c.isEnviarEmailXmlTransportadora,
            assuntoEmailXmlTransportadora: c.assuntoEmailXmlTransportadora,
            mensagemEmailXmlTransportadoraHtml: c.mensagemEmailXmlTransportadoraHtml || '',
            listTipoDocumentoImpressao: res.config.listTipoDocumentoImpressao || [],
          });
          this.form.markAsPristine();
          this.configCarregada = true;
        } else if (res.config?.isError) {
          this.alertService.show('Aviso', res.config.errorDescription, AlertType.warning);
        } else {
          this.alertService.show(
            'Aviso',
            'Não foi possível carregar as configurações da expedição. Recarregue a tela antes de salvar.',
            AlertType.warning
          );
        }
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Aviso', 'Falha na comunicação com o servidor.', AlertType.warning);
      },
    });
  }

  public get isSemValorDeclarado(): boolean {
    return this.form.get('idTipoValorDeclaradoExpedicao')?.value === TipoValorDeclaradoExpedicaoEnum.SemValorDeclarado;
  }

  /** Texto do campo de impressão automática: "Não imprimir" ou os nomes selecionados separados por vírgula. */
  public get resumoImpressaoAutomatica(): string {
    const selecionados = this.form.get('listTipoDocumentoImpressao')?.value as number[] || [];
    if (selecionados.length === 0) return 'Não imprimir';
    return this.opcoesTipoDocumentoImpressao
      .filter(t => selecionados.includes(t.idTipoDocumentoImpressao))
      .map(t => t.descricao)
      .join(', ');
  }

  public alternarVariaveis(bloco: BlocoEmail): void {
    bloco.exibirVariaveis = !bloco.exibirVariaveis;
  }

  /** Insere a variável na posição do cursor do editor, sem sobrescrever o conteúdo. */
  public inserirVariavel(bloco: BlocoEmail, codigo: string): void {
    bloco.editor.commands.insertText(codigo).focus().exec();
    this.form.get(bloco.controleMensagem)?.markAsDirty();
  }

  public salvar(): void {
    if (!this.configCarregada) {
      this.alertService.show('Atenção', 'As configurações não foram carregadas. Recarregue a tela antes de salvar.', AlertType.error);
      return;
    }

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.alertService.show('Atenção', 'Informe o assunto padrão de todos os e-mails.', AlertType.warning);
      return;
    }

    this.isSaving = true;
    const raw = this.form.getRawValue();
    const request: ConfigExpedicaoRequest = {
      isMostrarValorMercadoria: raw.isMostrarValorMercadoria,
      isHabilitarConferenciaPedido: raw.isHabilitarConferenciaPedido,
      isImprimirPedidoCanceladoListaColeta: raw.isImprimirPedidoCanceladoListaColeta,
      idTipoValorDeclaradoExpedicao: raw.idTipoValorDeclaradoExpedicao,
      isImprimirValorDeclaradoEtiqueta: raw.isImprimirValorDeclaradoEtiqueta,
      isEnviarEmailCodigoRastreio: raw.isEnviarEmailCodigoRastreio,
      assuntoEmailCodigoRastreio: raw.assuntoEmailCodigoRastreio,
      mensagemEmailCodigoRastreioHtml: raw.mensagemEmailCodigoRastreioHtml || null,
      isEnviarEmailEntrega: raw.isEnviarEmailEntrega,
      assuntoEmailEntrega: raw.assuntoEmailEntrega,
      mensagemEmailEntregaHtml: raw.mensagemEmailEntregaHtml || null,
      isEnviarEmailXmlTransportadora: raw.isEnviarEmailXmlTransportadora,
      assuntoEmailXmlTransportadora: raw.assuntoEmailXmlTransportadora,
      mensagemEmailXmlTransportadoraHtml: raw.mensagemEmailXmlTransportadoraHtml || null,
      listTipoDocumentoImpressao: raw.listTipoDocumentoImpressao || [],
    };

    this.configExpedicaoService.update(this.idConfigExpedicao, request).subscribe({
      next: response => {
        this.isSaving = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }
        this.form.markAsPristine();
        this.alertService.show('Sucesso', 'Configurações da expedição atualizadas.', AlertType.success);
      },
      error: error => {
        this.isSaving = false;
        this.alertService.show('Aviso', error || 'Falha na comunicação com o servidor.', AlertType.warning);
      },
    });
  }

  public voltar(): void {
    this.router.navigate([this.origem === 'gerais' ? '/configuracoes/vendas/gerais' : '/configuracoes/vendas']);
  }
}
